/** Every user-facing string lives here (see SPEC §2 non-goals: future i18n). */
export const strings = {
  appName: 'Kinklist',
  tagline: 'Rate your kinks privately. Your list never leaves your device unless you share it.',
  close: 'Close',
  dismiss: 'Dismiss',
  cancel: 'Cancel',
  save: 'Save',
  delete: 'Delete',
  undo: 'Undo',
  notifications: 'Notifications',
  theme: 'Theme',
  themeSystem: 'System',
  themeLight: 'Light',
  themeDark: 'Dark',
  saveFailed: "Couldn't save your list on this device. Storage may be full or disabled.",
  loadRecovered:
    "Your saved list couldn't be read, so you're starting fresh. The old data was kept aside.",
  updatedInOtherTab: 'Your list was updated in another tab.',

  yourName: 'Your name',
  moreOptions: 'More options',
  legend: 'Rating legend',
  searchLabel: 'Search kinks',
  searchPlaceholder: 'Search…',
  clearSearch: 'Clear search',
  filterLabel: 'Show',
  filterAll: 'All',
  filterUnrated: 'Unrated',
  filterCommented: 'Has comment',
  expandAll: 'Expand all',
  collapseAll: 'Collapse all',
  noMatches: 'Nothing matches your search or filter.',
  clearFilters: 'Clear search and filters',
  emptyCategory: 'No kinks in this category yet.',

  resetList: 'Reset list…',
  resetTitle: 'Reset your list?',
  resetDescription:
    'All ratings, comments and changes to the list are replaced by the default list. You can undo this until you reload the page.',
  resetConfirm: 'Reset list',
  resetDone: 'List reset',
  clearRatings: 'Clear all ratings…',
  clearTitle: 'Clear all ratings and comments?',
  clearDescription:
    'Your categories and kinks stay as they are; every rating goes back to the first rating and all comments are removed. You can undo this until you reload the page.',
  clearConfirm: 'Clear ratings',
  clearDone: 'Ratings cleared',

  commentLabel: 'Comment',
  commentDeleted: 'Comment deleted',

  share: 'Share',
  import: 'Import…',
  about: 'About',
  aboutTitle: 'About Kinklist',
  aboutSummary: 'Rate your kinks privately, then share the result as a link or an image.',
  aboutPrivacyTitle: 'Privacy',
  aboutPrivacy:
    'Your list never leaves your device unless you share it. It is saved in this browser only. Share links carry the list after the #, which browsers never send to servers. The app makes no network requests, except to fetch the image when you open an old Kinklist v2 (Imgur) link.',
  aboutCreditsTitle: 'Credits',
  aboutCredits: 'Based on Kinklist v2 by Goctionni, released under the MIT license.',
  aboutOriginal: 'Original Kinklist',
  aboutSource: 'Source code',
  aboutLicense: 'MIT license © 2026 Hanna Rose',
  hintTitle: 'How it works',
  hint: 'Tap a color to rate each kink, separately for each role (like Self and Partner). Your progress saves automatically on this device.',
  dismissHint: 'Got it',
  importTitle: 'Import a list',
  importDescription:
    'Open a Kinklist image (from this app or the old Kinklist), a kinklist.json file, or a share link.',
  chooseFile: 'Choose image or file…',
  pasteLinkLabel: 'Or paste a share link',
  openLink: 'Open',
  notALink: "That doesn't look like a Kinklist share link.",
  dropHere: 'Drop a Kinklist image or file to import it',
  loadingLegacy: 'Loading the shared list…',
  legacyFailedTitle: "Couldn't open this list",
  startFresh: 'Start fresh',
  shareTitle: 'Share your list',
  shareLinkLabel: 'Link to your list',
  shareLinkHint:
    'The whole list is inside the link, after the #. That part is never sent to any server; only people you give the link to can see it.',
  copyLink: 'Copy link',
  includeComments: 'Include comments',
  imageSection: 'Image',
  imageHint: 'A picture of your list to post or send anywhere.',
  imageTheme: 'Image colors',
  footnotes: 'Comments as footnotes',
  embedData: 'Embed the list in the image',
  embedHint: 'Lets anyone with the image import it back into Kinklist.',
  downloadImage: 'Download image',
  imageFailed: "Couldn't create the image.",
  dataSection: 'Data',
  dataHint: 'The complete list as a file, for backups or moving to another device.',
  downloadData: 'Download kinklist.json',
  textSection: 'Text',
  textHint: 'A summary grouped by rating, for chats and posts.',
  copyText: 'Copy as text',
  textCopied: 'Summary copied',
  linkCopied: 'Link copied',
  copyFailed: 'Copying failed. Select the link and copy it manually.',
  longLinkWarning:
    'This link is long. Some chat apps cut off long links; if it arrives broken, send the image instead.',
  openSharedTitle: 'Open the shared list?',
  openSharedDescription:
    'This replaces your current list, including your ratings and comments. You can undo it until you reload the page.',
  openShared: 'Replace my list',
  downloadCurrent: 'Download mine first',
  sharedOpened: 'Opened the shared list',
  editList: 'Edit list',
  editingTitle: 'Editing list',
  editingHint: 'Rename, reorder, add or delete categories, kinks and ratings.',
  done: 'Done',
  addCategory: 'Add category',
  addCategoryTitle: 'New category',
  editCategoryTitle: 'Edit category',
  categoryName: 'Category name',
  roles: 'Roles',
  rolesHint: 'Perspectives rated separately, e.g. Giving and Receiving. 1 to 4.',
  roleName: 'Role name',
  addRole: 'Add role',
  addKink: 'Add',
  addKinkPlaceholder: 'Add a kink…',
  newKinkLabel: 'New kink name',
  customizeRatings: 'Customize ratings',
  ratingsTitle: 'Ratings',
  ratingsDescription:
    'The first rating means "not rated yet". Rename, recolor, add, remove or reorder the rest.',
  ratingName: 'Rating name',
  ratingColor: 'Color',
  addRating: 'Add rating',
  resetRatings: 'Reset to defaults',
  ratingsReset: 'Ratings reset to defaults',
  colorblindNote:
    'Pick colors that differ in lightness as well as hue, so everyone can tell them apart. The defaults are colorblind-friendly.',
  reorderDisabled: 'Clear search and filters to reorder.',
  newRatingName: 'New rating',
  newCategoryName: 'New category',
  defaultRole: 'General',
} as const;

export type StringKey = keyof typeof strings;

export const importErrors = {
  'too-large': 'That is too big to be a Kinklist.',
  'unsupported-file': 'Choose a PNG image or a JSON file exported from Kinklist.',
  'no-data': "This image doesn't contain Kinklist data.",
  corrupt: 'This link or file is damaged or incomplete.',
  invalid: "This doesn't look like a Kinklist.",
  'not-found': 'That image no longer exists.',
  network: "Couldn't load the image. Check your connection and try again.",
} as const;

export const format = {
  progress: (rated: number, total: number) => `${String(rated)} / ${String(total)} rated`,
  progressShort: (rated: number, total: number) => `${String(rated)}/${String(total)}`,
  progressLabel: (rated: number, total: number) => `${String(rated)} of ${String(total)} rated`,
  pickerLabel: (kink: string, role?: string) => (role ? `${kink}, ${role}` : kink),
  addComment: (kink: string) => `Add comment on ${kink}`,
  editComment: (kink: string) => `Edit comment on ${kink}`,
  commentTitle: (category: string, kink: string) => `Comment: ${category} / ${kink}`,
  characters: (count: number, max: number) => `${String(count)} / ${String(max)}`,
  linkLength: (length: number) => `${length.toLocaleString('en')} characters`,
  version: (version: string) => `Version ${version}`,
  toggleCategory: (name: string, expanded: boolean) =>
    `${expanded ? 'Collapse' : 'Expand'} ${name}`,
  rename: (name: string) => `Rename ${name}`,
  remove: (name: string) => `Delete ${name}`,
  reorder: (name: string) => `Reorder ${name}`,
  editRoles: (name: string) => `Edit ${name}`,
  removeRole: (name: string) => `Remove role ${name}`,
  moveUp: (name: string) => `Move ${name} up`,
  moveDown: (name: string) => `Move ${name} down`,
  deleted: (name: string) => `Deleted ${name}`,
  confusable: (a: string, b: string, vision: string) =>
    vision === 'typical color vision'
      ? `${a} and ${b} look almost the same.`
      : `${a} and ${b} may be hard to tell apart with ${vision}.`,
  lifted: (name: string, position: number, count: number) =>
    `Picked up ${name}, position ${String(position)} of ${String(count)}. Use the arrow keys to move, Space to drop, Escape to cancel.`,
  moved: (name: string, position: number, count: number) =>
    `${name}, position ${String(position)} of ${String(count)}.`,
  dropped: (name: string, position: number, count: number) =>
    `Dropped ${name} at position ${String(position)} of ${String(count)}.`,
  cancelled: (name: string) => `Reordering cancelled. ${name} is back where it was.`,
};

/** Plain-language names for simulated color vision deficiencies. */
export const visionNames = {
  typical: 'typical color vision',
  protanopia: 'red-blind (protan) color vision',
  deuteranopia: 'green-blind (deutan) color vision',
  tritanopia: 'blue-blind (tritan) color vision',
} as const;

export const reorderMessages = {
  lifted: format.lifted,
  moved: format.moved,
  dropped: format.dropped,
  cancelled: format.cancelled,
};
